import { utcToZonedTime, format } from 'date-fns-tz';
import { colors, fontSizeSmall } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import type { IntlShape } from '@atlassian/jira-intl';
import {
	formatEstimateForTooltip,
	ESTIMATION_STORY_POINTS,
	ESTIMATION_ISSUE_COUNT,
	ESTIMATION_ORIGINAL_ESTIMATE,
} from '@atlassian/jira-software-report-charting-common/src/common/utils/format-estimate/index.tsx';
import type { TimeTrackingOptions } from '@atlassian/jira-time-tracking-formatter/src/types.tsx';
import { COMPLETION_SCATTER_SERIES } from '../../../common/constants';
import commonMessages from '../../../common/messages';
import type {
	ScatterPoint,
	SprintReportsEstimationStatistic,
	SprintScopeChangeData,
} from '../../../common/types';
import {
	getTimestamp,
	type SprintEvent,
	getCompletion,
	getScope,
} from '../../../common/utils/sprint-event';
import { getTooltipText } from './get-tooltip-text';
import messages from './messages';

type TooltipOptions = {
	intl: IntlShape;
	statisticField: null | SprintReportsEstimationStatistic;
	userTimeZone: string;
	timeTrackingOptions: TimeTrackingOptions;
};

/**
 * This type is dubious because ECharts sometimes will call our handler with the line's data.
 */
type TooltipArgument = null | {
	data: null | ScatterPoint;
	seriesName: string;
};

const getMessageByEstimationStatistic = (
	estimationStatistic: null | SprintReportsEstimationStatistic,
) => {
	switch (estimationStatistic) {
		case ESTIMATION_STORY_POINTS:
			return commonMessages.optionStatisticStoryPoints;

		case ESTIMATION_ORIGINAL_ESTIMATE:
			return messages.statisticOriginalEstimate;

		default:
			return commonMessages.optionStatisticIssueCount;
	}
};

export const renderScopeContent = (
	scopeChange: SprintScopeChangeData,
	isCompletion: boolean,
	intl: IntlShape,
	statisticField: null | SprintReportsEstimationStatistic,
	timeTrackingOptions: TimeTrackingOptions,
): string => {
	const deltaChange = isCompletion
		? scopeChange.completion - scopeChange.prevCompletion
		: scopeChange.scope - scopeChange.prevScope;
	const deltaChangeAsString = formatEstimateForTooltip(
		Math.abs(deltaChange),
		timeTrackingOptions,
		statisticField,
		intl,
	);

	const formattedDelta = deltaChange > 0 ? `+${deltaChangeAsString}` : `-${deltaChangeAsString}`;
	const deltaMessage = `${intl.formatMessage(
		getMessageByEstimationStatistic(statisticField),
	)} ${formattedDelta}`;
	const tooltipText = getTooltipText({ intl, event: scopeChange });

	return `
        ${tooltipText}
        <br />
        <span>${deltaMessage}</span>
    `;
};

export const renderStartEndContent = (
	isCompletion: boolean,
	statisticField: null | SprintReportsEstimationStatistic,
	timeTrackingOptions: TimeTrackingOptions,
	intl: IntlShape,
	event: SprintEvent,
) => {
	const statistic = isCompletion ? getCompletion(event) : getScope(event);
	const formattedStatistic = formatEstimateForTooltip(
		statistic,
		timeTrackingOptions,
		statisticField,
		intl,
	);

	const storyPointsOrOriginalEstimateMessage =
		statisticField !== ESTIMATION_ISSUE_COUNT
			? `${intl.formatMessage(
					getMessageByEstimationStatistic(statisticField),
				)} ${formattedStatistic}`
			: '';
	// Dont want issuelist on start of completion line
	const issueCount =
		event.type === 'end' || (event.type === 'start' && !isCompletion)
			? event.data.issueList.length
			: 0;
	const issueCountMessage = `${intl.formatMessage(
		commonMessages.optionStatisticIssueCount,
	)} ${issueCount}`;

	return `
        <b>
          ${intl.formatMessage(
						event.type === 'start' ? messages.sprintStarted : messages.sprintEnded,
					)}
        </b>
        <br />
        ${storyPointsOrOriginalEstimateMessage}
        <br />
        ${issueCountMessage}
    `;
};

export const renderTooltipContent = (
	intl: IntlShape,
	statisticField: null | SprintReportsEstimationStatistic,
	timeTrackingOptions: TimeTrackingOptions,
	event: SprintEvent,
	isCompletion: boolean,
): string => {
	if (event.type === 'start' || event.type === 'end') {
		return renderStartEndContent(isCompletion, statisticField, timeTrackingOptions, intl, event);
	}

	if (event.type !== 'scope') {
		return '';
	}
	return renderScopeContent(event.data, isCompletion, intl, statisticField, timeTrackingOptions);
};

/**
 * Creates a tooltip renderer for ECharts.
 */
export const makeRenderTooltip =
	({ userTimeZone, intl, statisticField, timeTrackingOptions }: TooltipOptions) =>
	(params: TooltipArgument): string => {
		if (!params || !params.data || !params.data.meta) {
			return '';
		}
		const { event } = params.data.meta;

		if (!event) {
			return '';
		}
		const tooltipContent = renderTooltipContent(
			intl,
			statisticField,
			timeTrackingOptions,
			event,
			params.seriesName === COMPLETION_SCATTER_SERIES,
		);

		const formattedTimestamp = format(
			utcToZonedTime(getTimestamp(event), userTimeZone),
			'dd/MMM/yy h:mm aa',
		);

		const color =
			params.seriesName === COMPLETION_SCATTER_SERIES
				? token('color.text.success', colors.G400)
				: token('color.text.danger', colors.R400);

		return `<div style="padding: 8px; border-left: 8px solid ${color}">
        <div>
            ${tooltipContent}
        </div>

        <span style="color: ${token(
					'color.text.subtlest',
					colors.N90,
				)}; font-weight: 400; font-size: ${fontSizeSmall()}px;">
            ${formattedTimestamp}
        </span>
    </div>`;
	};
